import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { Table } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { GetMessageQuery as GetMessage } from '__generated__/graphql';

import { Icons } from '../../../common/Tiptap/Layout';
import { VARIABLE_REGEX } from '../../../common/Tiptap/VariableHighlighter';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import EditMessageMutation from './EditMessageMutation';
import Editor from '../../../common/Tiptap';
import MailEditorLayout from '../../Common/MailEditorLayout';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useScroll from '../../../common/useScroll';
import useValidation from '../../../common/useValidation';

type Message = GetMessage['project']['message'];

const messageFormSchema = yup.object().shape({
  subject: yup.string().min(1).max(255).required(),
  content: yup.string().min(1).required(),
});

export interface EditMessageContentFormProps {
  message: Message;
}

const EditMessageContentForm = ({ message }: EditMessageContentFormProps) => {
  const { t } = useTranslation();
  const { scrollTo } = useScroll();
  const project = useProject();
  const notifier = useNotifier();

  const getValues = (message: Partial<Message>) => ({
    id: message.id,
    subject: message.subject,
    content: message.content,
  });

  const form = useForm({
    values: getValues(message),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editMessage, { error, loading }] = useMutation(EditMessageMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ editMessage: message }) => {
      notifier.success(t('notifications.message_saved'));
      form.reset(getValues(message));
      setFormKey((key) => key + 1);
    },
    onError: () => {
      scrollTo('MessageTab');
    },
  });

  const { valid, errors } = useValidation({
    schema: messageFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const defaultPlaceholders = [
    'first_name',
    'last_name',
    'full_name',
    'email',
  ];
  const registrationPlaceholders = message.allowed_placeholders.filter(
    (placeholder) => !defaultPlaceholders.includes(placeholder),
  );
  const usedPlaceholders = Array.from((form.values.content || '').matchAll(VARIABLE_REGEX)).map((match) => match[1]);
  const includesSummary = usedPlaceholders.some((placeholder) => registrationPlaceholders.includes(placeholder));

  return (
    <>
      <UI.Div sc={{ mb: 4 }}>
        <UI.SectionHeading sc={{ mb: 0 }}>
          {t('content')}
        </UI.SectionHeading>
        <UI.InputDescription>
          {t('message_content_intro')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card>
        <UI.Form onSubmit={() => editMessage()} key={formKey}>
          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.InputGroup sc={{ valid: !errors.subject, touched: form.touched.subject }}>
              <UI.InputLabel htmlFor="MessageSubject">
                {t('subject')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput
                {...form.text('subject')}
                id="MessageSubject"
                sc={{ size: 'lg' }}
              />
              <UI.ErrorMessages attribute={t('subject')} errors={errors.subject} />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.content, touched: form.touched.content }}>
              <UI.InputLabel id="MessageContentLabel">
                {t('content')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputDescription>
                {t('message_content_description')}
              </UI.InputDescription>
              <MailEditorLayout
                editor={(props) => (
                  <Editor
                    value={form.values.content}
                    onChange={(value) => form.set('content', value)}
                    variables={message.allowed_placeholders}
                    options="full"
                    attributes={{
                      'aria-labelledby': 'MessageContentLabel',
                      style: 'min-height: 300px',
                    }}
                    {...props}
                  />
                )}
                value={form.values.content}
                event={message.event}
                showRegistrationSummary
              />
            </UI.InputGroup>

            {message.allowed_placeholders.includes('ticket') && (
              <UI.FormGrid sc={{ gutter: 0.5 }}>
                {includesSummary && (
                  <UI.Info icon={<Table />}>
                    {t('message_multiple_tickets_hint')}
                  </UI.Info>
                )}

                <UI.Info icon={<Icons.Variable />}>
                  <Trans i18nKey="message_variables_hint">
                    <Link to={`/${project.id}/communication/messages/${message.id}`} />
                  </Trans>
                </UI.Info>
              </UI.FormGrid>
            )}

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_message')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.Form>
      </UI.Card>
    </>
  );
};

export default EditMessageContentForm;
